import React, { useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Outlet, useNavigate, Link } from "react-router-dom";
import Loading from "../components/UI/Loading/Loading";
import { useEffect } from "react";
import { io } from "socket.io-client";
import axios from "axios";
import { queryClient } from "../App";
import chat_notification from "../assets/chat_notification.wav";
import notification_sweet from "../assets/notification_sweet.wav";
import { setOnlineUsers } from "../app/slices/soketSlice";
import { toast } from "react-hot-toast";
import Notification from "../components/UI/Notification/Notification";
import { logout, reciveNoti, updateNStats } from "../app/slices/userSlice";
import {
  updateChatName,
  updateChatTheme,
  updateMessages,
  updateSeenMessages,
} from "../utils/rqUpdate";
import { backendUrl, apiUrl } from "../utils/backendUrl";
import Topbar from "../components/layout/Topbar";
import SubNavbar from "../components/layout/SubNavbar";
import Footer from "../components/layout/Footer";
import ChatSidebar from "../components/layout/ChatSidebar";
import ChatPopupManager from "../components/chat/ChatPopupManager";
import { MiniChatProvider, miniChatBus } from "../context/MiniChatContext";
import { createRingtone } from "../utils/ringtone";

export let socket;

function playNotificationSound() {
  const audio = new Audio(chat_notification);
  audio.play();
}

function playNotificationSound2() {
  const audio = new Audio(notification_sweet);
  audio.play();
}

export default function IsLoggedIn({ user }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const onlineUsers = useSelector((state) => state.soketSlice?.onlineUsers || []);

  const handleLogout = () => {
    dispatch(logout());
    if (socket?.connected) {
      socket.disconnect();
    }
    navigate("/login");
  };

  const msgNoti = (newMessage) => {
    const res = window.location.pathname.split("/").pop() !== newMessage.chat;
    return res;
  };

  // ── Ringtone (looping ring for incoming calls) ─────────────────────
  const ringtoneRef = useRef(null);
  const [incomingCall, setIncomingCall] = useState(null); // { callId, roomId, type, caller }

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.stop();
      ringtoneRef.current = null;
    }
  }, []);

  const startRingtone = useCallback(() => {
    stopRingtone();
    ringtoneRef.current = createRingtone();
    ringtoneRef.current.start();
  }, [stopRingtone]);

  useEffect(() => {
    if (user.userinfo) {
      socket = io(backendUrl);

      // Emit setup on every (re)connection so the server registers this
      // user as online even after server restarts / deploys.
      const emitSetup = () => {
        socket.emit("setup", {
          userId: user.userinfo._id,
          info: {
            id: user.userinfo._id,
            username: user.userinfo.username,
            first_name: user.userinfo.first_name,
            last_name: user.userinfo.last_name,
            photo: user.userinfo.photo,
          },
        });
      };
      socket.on("connect", emitSetup);

      socket.on("online_user", ({ type, info }) => {
        dispatch(setOnlineUsers({ type, info }));
      });

      socket.on("new_notification", ({ notification }) => {
        playNotificationSound2();
        dispatch(
          reciveNoti({
            type: "notification",
          })
        );

        toast.custom((t) => (
          <Notification t={t} toast={toast} notification={notification} />
        ));

        queryClient.setQueryData(["getNotifications"], (oldData) => {
          if (!oldData) return oldData;
          let newData = oldData;
          newData.data.notifications = [
            notification,
            ...newData.data.notifications,
          ];
          return {
            ...oldData,
            newData,
          };
        });
      });

      socket.on("message_recieved", (newMessage) => {
        // Route to any open mini chat for this sender
        const senderId = String(newMessage?.sender?._id || newMessage?.sender?.id || "");
        const hasMiniChat = miniChatBus.hasChatForSender(senderId);
        miniChatBus.emit(newMessage);

        // Only play sound + show toast if no mini chat is open for this sender
        if (!hasMiniChat) {
          playNotificationSound();
          dispatch(
            reciveNoti({
              type: "message",
            })
          );
          if (msgNoti(newMessage)) {
            toast.custom((t) => (
              <Notification
                t={t}
                toast={toast}
                notification={{
                  sender: newMessage.sender,
                  type: "message",
                  click: `/messages/message/${newMessage.sender?.username || ""}`,
                  content: `${newMessage.sender.first_name} ${newMessage.sender.last_name} sent you a message`,
                  createdAt: newMessage.createdAt,
                }}
              />
            ));
          }
        }
        updateMessages(newMessage);
      });

      socket.on("customize_chat", ({ type, content, chat }) => {
        if (type === "theme") {
          updateChatTheme(content, chat);
        }
        if (type === "name") {
          updateChatName(content, chat);
        }
      });

      socket.on("seen", ({ message: newMessage, chat }) => {
        updateSeenMessages(newMessage, chat);
      });

      // ── Incoming call handler ──────────────────────────────────
      socket.on("incoming_call", (callData) => {
        startRingtone(); // continuous ringing until accept/reject
        setIncomingCall(callData);
      });

      socket.on("call_ended", () => {
        stopRingtone();
        setIncomingCall(null);
      });

      socket.on("call_rejected", () => {
        stopRingtone();
        // Caller sees this — could show a toast
        toast.error("Call was rejected");
      });
    }
  }, []);

  // Stop ringtone whenever incomingCall is cleared (safety net)
  useEffect(() => {
    if (!incomingCall) stopRingtone();
  }, [incomingCall, stopRingtone]);

  // Cleanup ringtone on unmount
  useEffect(() => {
    return () => stopRingtone();
  }, [stopRingtone]);

  // ── Dropdown data ──────────────────────────────────────────────────
  const [notiItems, setNotiItems] = useState(null);   // null = not loaded yet
  const [frReqItems, setFrReqItems] = useState(null); // null = not loaded yet
  const [msgItems, setMsgItems] = useState(null);     // null = not loaded yet

  // Eagerly fetch notifications and friend requests on mount so data
  // is ready before the user clicks the dropdown icons.
  useEffect(() => {
    if (!user.userinfo) return;
    axios
      .get(apiUrl("/api/v1/notifications?page=1&limit=5"), { withCredentials: true })
      .then((res) => setNotiItems(res.data?.data?.notifications || []))
      .catch(() => setNotiItems([]));
    axios
      .get(apiUrl("/api/v1/friends/requests?page=1&limit=5"), { withCredentials: true })
      .then((res) => setFrReqItems(res.data?.data?.requests || []))
      .catch(() => setFrReqItems([]));
    axios
      .get(apiUrl("/api/v1/messages/conversations"), { withCredentials: true })
      .then((res) => setMsgItems((res.data?.data?.conversations || []).slice(0, 5)))
      .catch(() => setMsgItems([]));
  }, [user.userinfo]);

  // Re-fetch when a dropdown is opened (to pick up new items)
  const handleOpenBox = useCallback(
    async (type) => {
      if (type === "notifications") {
        try {
          const res = await axios.get(
            apiUrl("/api/v1/notifications?page=1&limit=5"),
            { withCredentials: true }
          );
          setNotiItems(res.data?.data?.notifications || []);
        } catch {
          // best-effort
        }
      }
      if (type === "friends") {
        try {
          const res = await axios.get(
            apiUrl("/api/v1/friends/requests?page=1&limit=5"),
            { withCredentials: true }
          );
          setFrReqItems(res.data?.data?.requests || []);
        } catch {
          // best-effort
        }
      }
      if (type === "messages") {
        try {
          const res = await axios.get(
            apiUrl("/api/v1/messages/conversations"),
            { withCredentials: true }
          );
          setMsgItems((res.data?.data?.conversations || []).slice(0, 5));
        } catch {
          // best-effort
        }
      }
    },
    []
  );

  // ── Accept / Reject friend request from dropdown ─────────────────
  const handleAcceptRequest = useCallback(
    async (senderId) => {
      try {
        await axios.post(
          apiUrl(`/api/v1/friends/accept/${senderId}`),
          {},
          { withCredentials: true }
        );
        // Remove from local list
        setFrReqItems((prev) => (prev || []).filter((r) => {
          const s = r.requester || r.sender || {};
          return String(s._id) !== String(senderId);
        }));
        // Decrement badge count in Redux
        const currentCount = user.userinfo?.recivedRequestsCount || 0;
        dispatch(updateNStats({ recivedRequestsCount: Math.max(0, currentCount - 1) }));
        toast.success("Friend request accepted");
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to accept request");
      }
    },
    [dispatch, user.userinfo?.recivedRequestsCount]
  );

  const handleRejectRequest = useCallback(
    async (senderId) => {
      try {
        await axios.delete(
          apiUrl(`/api/v1/friends/reject/${senderId}`),
          { withCredentials: true }
        );
        // Remove from local list
        setFrReqItems((prev) => (prev || []).filter((r) => {
          const s = r.requester || r.sender || {};
          return String(s._id) !== String(senderId);
        }));
        // Decrement badge count in Redux
        const currentCount = user.userinfo?.recivedRequestsCount || 0;
        dispatch(updateNStats({ recivedRequestsCount: Math.max(0, currentCount - 1) }));
        toast.success("Friend request rejected");
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to reject request");
      }
    },
    [dispatch, user.userinfo?.recivedRequestsCount]
  );

  // ── Incoming call handlers ──────────────────────────────────────
  const handleAcceptCall = useCallback(
    async () => {
      if (!incomingCall?.callId) return;
      stopRingtone();
      try {
        await axios.post(apiUrl(`/api/v1/calls/${incomingCall.callId}/accept`), {}, { withCredentials: true });
        setIncomingCall(null);
        navigate(`/call/${incomingCall.callId}`);
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to accept call");
        setIncomingCall(null);
      }
    },
    [incomingCall, navigate, stopRingtone]
  );

  const handleRejectCall = useCallback(
    async () => {
      if (!incomingCall?.callId) return;
      stopRingtone();
      try {
        await axios.post(apiUrl(`/api/v1/calls/${incomingCall.callId}/reject`), {}, { withCredentials: true });
      } catch (_) {}
      setIncomingCall(null);
    },
    [incomingCall, stopRingtone]
  );

  // ── Render dropdown items ────────────────────────────────────────
  const renderNotiItem = (n) => {
    const actor = n.actor || n.sender || {};
    const name = `${actor.firstName || actor.first_name || ""} ${actor.lastName || actor.last_name || ""}`.trim() || actor.username || "Someone";
    const photo = actor.photo || "/images/default_pic.png";
    const text = n.message || n.content || `${name} sent you a notification`;
    const link = n.click || `/notifications/all`;
    return (
      <li key={n._id} style={{ borderBottom: "1px solid #eee" }}>
        <Link
          to={link}
          style={{ display: "flex", alignItems: "center", padding: "8px 12px", textDecoration: "none", color: "#333", gap: 8 }}
        >
          <img src={photo} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} />
          <div style={{ fontSize: 13, lineHeight: 1.3 }}>{text}</div>
        </Link>
      </li>
    );
  };

  const notificationsDropdown =
    notiItems === null ? (
      <div style={{ padding: "10px 12px", color: "#999", fontSize: 13 }}>Loading...</div>
    ) : notiItems.length > 0 ? (
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {notiItems.map(renderNotiItem)}
      </ul>
    ) : null;

  const friendsDropdown =
    frReqItems === null ? (
      <div style={{ padding: "10px 12px", color: "#999", fontSize: 13 }}>Loading...</div>
    ) : frReqItems.length > 0 ? (
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {frReqItems.map((r) => {
          const s = r.requester || r.sender || {};
          const senderId = s._id;
          const name = `${s.firstName || s.first_name || ""} ${s.lastName || s.last_name || ""}`.trim() || s.username || "Someone";
          const photo = s.photo || "/images/default_pic.png";
          return (
            <li key={r._id} style={{ borderBottom: "1px solid #eee", padding: "8px 12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Link to={`/u/${s.username}`} style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none", color: "#333", flex: 1, minWidth: 0 }}>
                  <img src={photo} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                  <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                </Link>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleAcceptRequest(senderId); }}
                    title="Accept"
                    style={{
                      width: 32, height: 32, borderRadius: "50%", border: "none",
                      backgroundColor: "#4caf50", color: "#fff", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 16, fontWeight: 700, lineHeight: 1,
                    }}
                  >
                    ✓
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRejectRequest(senderId); }}
                    title="Reject"
                    style={{
                      width: 32, height: 32, borderRadius: "50%", border: "none",
                      backgroundColor: "#e53935", color: "#fff", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 16, fontWeight: 700, lineHeight: 1,
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    ) : null;

  const messagesDropdown =
    msgItems === null ? (
      <div style={{ padding: "10px 12px", color: "#999", fontSize: 13 }}>Loading...</div>
    ) : msgItems.length > 0 ? (
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {msgItems.map((c) => {
          // MERN conversations API: participants[] has current user + partner(s)
          const myId = String(user.userinfo?._id || "");
          const other = (c.participants || []).find((p) => String(p._id) !== myId) || c.participants?.[0] || {};
          const name = `${other.first_name || other.firstName || ""} ${other.last_name || other.lastName || ""}`.trim() || other.username || c.name || "Chat";
          const photo = other.photo || "/images/default_pic.png";
          const lastMsg = c.lastMessage;
          const snippet = lastMsg?.content
            ? (lastMsg.content.length > 40 ? lastMsg.content.slice(0, 40) + "…" : lastMsg.content)
            : "";
          const link = `/messages/message/${other.username || c._id}`;
          return (
            <li key={c._id} style={{ borderBottom: "1px solid #eee" }}>
              <Link
                to={link}
                style={{ display: "flex", alignItems: "center", padding: "8px 12px", textDecoration: "none", color: "#333", gap: 8 }}
              >
                <img src={photo} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                  {snippet && (
                    <div style={{ fontSize: 12, color: "#777", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>{snippet}</div>
                  )}
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    ) : null;

  return user.userinfo ? (
    <MiniChatProvider user={user.userinfo}>
      <Topbar user={user.userinfo} onLogout={handleLogout} />
      <SubNavbar
        user={user.userinfo}
        friendRequests={user.userinfo?.recivedRequestsCount || 0}
        messageCount={user.userinfo?.unseenMessages || 0}
        notifCount={user.userinfo?.unseenNotification || 0}
        onOpenBox={handleOpenBox}
        notificationsDropdown={notificationsDropdown}
        friendsDropdown={friendsDropdown}
        messagesDropdown={messagesDropdown}
      />
      <React.Suspense fallback={<Loading />}>
        <Outlet />
      </React.Suspense>
      <Footer />
      <ChatSidebar onlineFriends={onlineUsers} />
      <ChatPopupManager />

      {/* ── Incoming call overlay ─────────────────────────────── */}
      {incomingCall && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.6)", zIndex: 10000,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {/* Pulse animation for accept button */}
          <style>{`
            @keyframes vds-pulse-ring {
              0% { box-shadow: 0 0 0 0 rgba(76,175,80,0.6); }
              70% { box-shadow: 0 0 0 16px rgba(76,175,80,0); }
              100% { box-shadow: 0 0 0 0 rgba(76,175,80,0); }
            }
            .vds-call-accept-btn {
              animation: vds-pulse-ring 1.5s ease-out infinite;
            }
          `}</style>
          <div style={{
            backgroundColor: "#fff", borderRadius: 16, padding: "32px 40px",
            textAlign: "center", minWidth: 300, boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          }}>
            <img
              src={incomingCall.caller?.photo || "/images/default_pic.png"}
              alt=""
              style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", marginBottom: 12 }}
            />
            <h3 style={{ margin: "0 0 4px", fontSize: 18 }}>
              {`${incomingCall.caller?.first_name || ""} ${incomingCall.caller?.last_name || ""}`.trim() || "Someone"}
            </h3>
            <p style={{ color: "#666", margin: "0 0 20px", fontSize: 14 }}>
              Incoming {incomingCall.type === "audio" ? "voice" : "video"} call...
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: 24 }}>
              <button
                onClick={handleRejectCall}
                style={{
                  width: 56, height: 56, borderRadius: "50%", border: "none",
                  backgroundColor: "#e53935", color: "#fff", fontSize: 22,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}
                title="Reject"
              >
                <i className="fa-solid fa-phone-slash"></i>
              </button>
              <button
                className="vds-call-accept-btn"
                onClick={handleAcceptCall}
                style={{
                  width: 56, height: 56, borderRadius: "50%", border: "none",
                  backgroundColor: "#4caf50", color: "#fff", fontSize: 22,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}
                title="Accept"
              >
                <i className="fa-solid fa-phone"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </MiniChatProvider>
  ) : (
    <Navigate to="/login" />
  );
}
